/**
 * 문장완성검사(SCT, A42): 손으로 쓴 검사지를 문항별 응답으로 옮긴다.
 * 손글씨 읽기는 상담자가 쓰는 외부 AI(구독)에 검사지 사진·PDF를 첨부해 맡기고, 앱은 문항 목록과 형식을 준다.
 * 응답은 쓴 그대로(맞춤법을 고치지 않음) 옮기고, 읽기 어려운 곳은 표시해 상담자가 확인·수정한다.
 * 이름·생년월일 같은 신상 칸은 옮기지 않는다.
 */
import type { AssessmentItem } from '../schema/v1';
import { parseJsonLoose, str, type ParseFail, type ParseOk } from '../ai/parse';
import { BEGIN, END } from '../ai/prompts';

export const SCT_READ_VERSION = 'SCT_READ_PACKET_V1';
export const SCT_FORMAT = 'SCT_ITEMS_V1';
export const SCT_TEST_NAME = '문장완성검사(SCT)';

/** 문항 목록: 한 줄에 한 문항. 앞의 "1." "1)" 같은 번호는 뗀다 */
export function parseStems(text: string): string[] {
  return text
    .replace(/\r/g, '')
    .split('\n')
    .map((l) => l.trim().replace(/^\d{1,3}\s*[.)]\s*/, ''))
    .filter(Boolean);
}

export function buildSctReadPacket(i: { clientCode: string; stems?: string[] | undefined }): string {
  const stems = i.stems?.filter((s) => s.trim()) ?? [];
  return [
    `=== ${SCT_READ_VERSION} ===`,
    `[검사] ${SCT_TEST_NAME} · 내담자 ${i.clientCode}`,
    stems.length
      ? `[문항 목록] (번호와 문항은 이 목록을 기준으로)\n${stems.map((s, k) => `${k + 1}. ${s}`).join('\n')}`
      : '[문항 목록] 없음 — 검사지에 인쇄된 번호와 문항(앞부분)을 그대로 옮긴다 (여러 쪽이면 모든 쪽의 문항을 번호 순으로).',
    `=== END ${SCT_READ_VERSION} ===`,
    '',
    '이 메시지에 첨부한 문장완성검사 검사지(사진·PDF, 모든 쪽)에서 문항마다 내담자가 손으로 쓴 응답을 옮겨라.',
    '- 응답은 쓴 그대로 옮긴다. 맞춤법·띄어쓰기를 고치지 않고, 해석하거나 요약하지 않는다. 지운 흔적·덧쓴 글자는 최종 글자로.',
    '- 읽기 어려운 글자는 [?]로 두고 그 문항은 unclear를 true로. 짐작한 글자가 있으면 [?짐작] 처럼.',
    '- 빈칸이면 response는 "" (빈 문자열). 문항을 빠뜨리지 말고 처음부터 끝까지 모두.',
    '- 이름·나이·성별·생년월일·학교·직장 같은 신상 칸은 옮기지 않는다. 검사지의 실시일이 있으면 administeredAt에 (YYYY-MM-DD).',
    `- 출력은 아래 JSON 하나만, ${BEGIN} 와 ${END} 사이에. 설명·인사말 없이 (아티팩트로 만들지 않는다):`,
    BEGIN,
    `{"formatVersion":"${SCT_FORMAT}","administeredAt":"YYYY-MM-DD (검사지에 날짜가 있으면, 없으면 빈 문자열)","items":[{"no":1,"stem":"나에게 이상한 일이 생겼을 때","response":"","unclear":false}]}`,
    END,
  ].join('\n');
}

export type SctRead = { administeredAt?: string; items: AssessmentItem[] };

/** 답 읽기: 번호 순, 같은 번호는 마지막 것. 문항 목록이 있으면 비어 있는 문항을 채운다 */
export function parseSctItems(raw: string, stems: string[] = []): ParseOk<SctRead> | ParseFail {
  const j = parseJsonLoose(raw);
  if (!j.ok) return { ok: false, errors: [j.error] };
  const o = (j.value && typeof j.value === 'object' ? j.value : {}) as Record<string, unknown>;
  const list = Array.isArray(o.items) ? o.items : [];
  const byNo = new Map<number, AssessmentItem>();
  for (const x of list) {
    if (!x || typeof x !== 'object') continue;
    const r = x as Record<string, unknown>;
    const no = Number(r.no);
    if (!Number.isInteger(no) || no < 1 || no > 500) continue;
    const stem = (str(r.stem) ?? stems[no - 1] ?? '').slice(0, 500);
    const response = typeof r.response === 'string' ? r.response.trim().slice(0, 3000) : '';
    const unclear = r.unclear === true || /\[\?/.test(response);
    byNo.set(no, { no, stem, response, ...(unclear ? { unclear: true } : {}) });
  }
  const items = [...byNo.values()].sort((a, b) => a.no - b.no);
  if (!items.length) return { ok: false, errors: ['문항별 응답(items)이 없습니다'] };
  const warnings = [
    ...(o.formatVersion === SCT_FORMAT ? [] : [`formatVersion 없음 (${SCT_FORMAT}로 간주)`]),
    ...(j.truncated ? ['응답이 중간에 끊겼습니다 — 받은 문항만 넣습니다'] : []),
    ...(stems.length && items.length < stems.length ? [`문항 ${stems.length}개 중 ${items.length}개만 왔어요`] : []),
  ];
  const date = str(o.administeredAt);
  return {
    ok: true,
    value: { items, ...(date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? { administeredAt: date } : {}) },
    warnings,
  };
}

export function sctSummary(items: AssessmentItem[]): string {
  const answered = items.filter((x) => x.response.trim()).length;
  const unclear = items.filter((x) => x.unclear).length;
  return `${items.length}문항 중 ${answered}개 응답${unclear ? ` · 손글씨 확인 필요 ${unclear}개` : ''} (문항별 응답은 별지)`;
}

/** 해석 요청·문서에 넣는 문항별 응답 줄 */
export const itemLine = (x: AssessmentItem) =>
  `${x.no}. ${x.stem} → ${x.response.trim() || '(무응답)'}${x.unclear ? ' (손글씨 확인 필요)' : ''}`;

/** 사례보고서 별지 표 (| 번호 | 문항 | 응답 |) — 표 칸을 나누는 | 는 바꿔 둔다 */
export function itemsTable(items: AssessmentItem[]): string {
  const cell = (s: string) =>
    s
      .replace(/\|/g, '｜')
      .replace(/\s*\n\s*/g, ' ')
      .trim();
  return [
    '| 번호 | 문항 | 응답 |',
    ...items.map(
      (x) => `| ${x.no} | ${cell(x.stem)} | ${cell(x.response) || '(무응답)'}${x.unclear ? ' (확인 필요)' : ''} |`,
    ),
  ].join('\n');
}
